import { FieldArray, FormikProvider, useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import add from 'src/assets/img/create/add.svg';
import remove from 'src/assets/img/create/remove.svg';
import sprite from 'src/assets/sprite.svg';
import { GetPreview } from 'src/components';
import { months } from 'src/constants';
import { useTranslation, useWindowDimensions } from 'src/hooks';
import { templatesActions } from 'src/store/actions';
import { setCvEducation } from 'src/store/reducers/templates.reducer';
import { v4 as uuidv4 } from 'uuid';

import CitySelector from './city-selector';
import Sidebar from './sidebar';

const Education = () => {
  const cvEducation = useSelector((state) => state.templatesReducer.cvEducation);
  const fixWaiter = useSelector((state) => state.templatesReducer.fixWaiter);
  const { translate } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const educationDegrees = useSelector((state) => state.templatesReducer.educationDegrees);
  const educationFields = useSelector((state) => state.templatesReducer.educationFields);
  const countries = useSelector((state) => state.templatesReducer.countries);
  const { id, educations, country, city } = useSelector(
    (state2) => state2.templatesReducer.currentResume,
  );
  const { width } = useWindowDimensions();
  const yearNow = new Date().getFullYear();

  const [preview, setPreview] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (preview) {
      setShowPreview(false);
      const file = new File([preview], 'file');
      dispatch(templatesActions.resumesUploadPreview({ image: file, id })).then(() => {
        navigate(`/my-cv/create/certifications/?id=${id}`);
      });
    }
  }, [preview]);

  const handleSubmit = async (values) => {
    await dispatch(templatesActions.resumeEducation(values));
    setShowPreview(true);
  };

  const [cvEducationState] = useState(cvEducation);

  const formik = useFormik({
    initialValues: cvEducationState || {
      id: id || '',
      education: educations.length
        ? educations
        : [
            {
              id: 0,
              universityName: '',
              country: { id: (country && country.id) || (countries[0] && countries[0].id) || '1' },
              city: { id: (city && city.id) || '1' },
              graduationMonth: '1',
              graduationYear: yearNow,
              degree: { id: 1 },
              field: { id: 1 },
              achievements: '',
            },
          ],
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  useEffect(() => {
    const handleSet = () => {
      if (id) {
        dispatch(setCvEducation(formik.values));
      }
    };

    return handleSet;
  }, [formik.values]);

  const changeCounty = (newId, index) => {
    formik.setFieldValue(`education[${index}].country.id`, newId);
  };

  const changeCity = (newId, index) => {
    formik.setFieldValue(`education[${index}].city.id`, newId);
  };

  const renderYearOptions = () => {
    const options = [];
    for (let i = yearNow; i > 1950; i -= 1) {
      options.push(
        <option value={i} key={i}>
          {i}
        </option>,
      );
    }
    return options;
  };

  const addNewRow = () => {
    const newEducation = [
      ...formik.values.education,
      {
        id: uuidv4(),
        universityName: '',
        country: { id: (country && country.id) || (countries[0] && countries[0].id) || '' },
        city: { id: (city && city.id) || '1' },
        graduationMonth: '1',
        graduationYear: yearNow,
        degree: { id: 1 },
        field: { id: 1 },
        achievements: '',
      },
    ];
    formik.setFieldValue('education', newEducation);
  };

  const removeRow = () => {
    formik.setFieldValue('education', formik.values.education.slice(0, -1));
  };

  return (
    <>
      {showPreview && <GetPreview setPreview={setPreview} />}
      <div className="create__mob">
        <Link to={`/my-cv/work/?id=${id}`} type="button" className="create__back">
          <svg width="24" height="24" viewBox="0 0 24 24">
            <use xlinkHref={`${sprite}#arr-back`} />
          </svg>
          {translate('cover_create.back')}
        </Link>

        <button type="button" className="create__mobPreview">
          <svg width="32" height="32" viewBox="0 0 32 32">
            <use xlinkHref={`${sprite}#preview`} />
          </svg>
        </button>
      </div>
      <div className="create__wrap">
        <Sidebar step={4} />
        <div className="create__right">
          <div className="create__title">{translate('cv_create.education')}</div>
          <div className="create__skip">
            <Link to={`/my-cv/create/certifications/?id=${id}`}>
              {translate('cover_create.skip_this_step')}
            </Link>
          </div>
          <form className="create__form" onSubmit={formik.handleSubmit}>
            <FormikProvider value={formik}>
              <FieldArray
                name="education"
                render={() => (
                  <div className="create__blocks">
                    {formik.values.education.map((item, index) => (
                      <div className="create__block" key={item.id}>
                        <div className="form-group">
                          <div className="row">
                            <div className="col-12 ">
                              <div className="label">
                                {translate('cv_create.college_university')}
                              </div>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Oxford University"
                                name={`education[${index}].universityName`}
                                defaultValue={formik.values.education[index].universityName}
                                onChange={formik.handleChange}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="form-group">
                          <div className="row">
                            <div className="col-12 col-sm-6">
                              <div className="label">{translate('cover_create.country')}</div>
                              <select
                                className="form-control"
                                defaultValue={formik.values.education[index].country.id}
                                name={`education[${index}].country.id`}
                                onChange={(e) => changeCounty(e.target.value, index)}
                              >
                                {countries.length &&
                                  countries.map((option) => (
                                    <option key={option.id} value={option.id}>
                                      {option.name}
                                    </option>
                                  ))}
                              </select>
                            </div>
                            <div className="col-12 col-sm-6">
                              <div className="label">{translate('cover_create.city')} </div>
                              {countries.length && (
                                <CitySelector
                                  countyId={String(
                                    formik.values.education[index].country.id || countries[0].id,
                                  )}
                                  index={index}
                                  changeCity={changeCity}
                                  value={String(formik.values.education[index].city.id)}
                                  name={`education[${index}].city.id`}
                                  setFieldValue={formik.setFieldValue}
                                />
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="form-group">
                          <div className="row">
                            <div className="col-12 col-sm-6">
                              <div className="label">{translate('cv_create.graduation_month')}</div>
                              <select
                                className="form-control"
                                name={`education[${index}].graduationMonth`}
                                defaultValue={formik.values.education[index].graduationMonth}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                              >
                                {months.map((option) => (
                                  <option value={option.id} key={option.id}>
                                    {option.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-12 col-sm-6">
                              <div className="label">{translate('cv_create.graduation_year')}</div>
                              <select
                                className="form-control"
                                name={`education[${index}].graduationYear`}
                                defaultValue={formik.values.education[index].graduationYear}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                              >
                                {renderYearOptions()}
                              </select>
                            </div>
                          </div>
                        </div>

                        <div className="form-group">
                          <div className="row">
                            <div className="col-12 col-sm-6">
                              <div className="label">{translate('cv_create.degree')}</div>
                              <select
                                className="form-control"
                                defaultValue={formik.values.education[index].degree.id}
                                name={`education[${index}.degree.id]`}
                                onChange={formik.handleChange}
                              >
                                {educationDegrees.length &&
                                  educationDegrees.map((option) => (
                                    <option key={option.id} value={option.id}>
                                      {option.name}
                                    </option>
                                  ))}
                              </select>
                            </div>
                            <div className="col-12 col-sm-6">
                              <div className="label">{translate('cv_create.field_study')}</div>
                              <select
                                className="form-control"
                                defaultValue={formik.values.education[index].field.id}
                                name={`education[${index}.field.id]`}
                                onChange={formik.handleChange}
                              >
                                {educationFields.length &&
                                  educationFields.map((option) => (
                                    <option key={option.id} value={option.id}>
                                      {option.name}
                                    </option>
                                  ))}
                              </select>
                            </div>
                          </div>
                        </div>

                        <div className="form-group">
                          <div className="row">
                            <div className="col-12 ">
                              <div className="label">
                                {translate('cv_create.your_achievements')}
                              </div>
                              <textarea
                                className="form-control"
                                placeholder="Your Achievements"
                                defaultValue={item.achievements}
                                name={`education[${index}].achievements`}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                              />
                            </div>
                          </div>
                        </div>
                        {index > 0 && index + 1 === formik.values.education.length && (
                          <button type="button" className="create__add mb-1" onClick={removeRow}>
                            <img src={remove} alt="add icon" />
                            Remove education
                          </button>
                        )}
                        {formik.values.education.length === index + 1 && index < 2 && (
                          <button type="button" className="create__add" onClick={addNewRow}>
                            <img src={add} alt="add icon" />
                            Add education
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              />
            </FormikProvider>
            <div className="create__sub">
              {width > 565 ? (
                <Link to={`/my-cv/create/work/?id=${id}`} className="btn btn-outline-primary">
                  Back
                </Link>
              ) : (
                <Link
                  to={`/my-cv/create/certifications/?id=${id}`}
                  className="btn btn-outline-primary"
                >
                  Skip this step
                </Link>
              )}

              <button type="submit" className="btn btn-primary" disabled={fixWaiter}>
                {fixWaiter ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    />
                    <span className="sr-only">{translate('loading')}</span>
                  </>
                ) : (
                  <>{translate('cover_create.continue')}</>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default Education;
